import { Grid } from '@mui/material';
import { ChangeEvent, FC, useCallback, useState } from 'react';
import Textinput from './Textinput';
import MediumTypography from './MediumTypography';
import { FormikErrors, FormikTouched } from 'formik';
import SmallTypography from './SmallTypography';
import { Address } from '../../pages/employees/types';
import {
  ZipCodeResponse,
  getZipCodeDetails,
} from '../../services/configApi/User';
import { OptionType, ZipCodeType } from '../../utils/type';
import AutocompleteSearch from './AutocompleteSearch';
import { debounce } from 'lodash';

interface AddressFormPropType {
  headerTitleId?: string;
  address1PlaceholderId: string;
  address2PlaceholderId: string;
  zipCodePlaceholderId: string;
  cityPlaceholderId: string;
  statePlaceholderId: string;
  value: Address;
  isClient: boolean;
  disableForm: boolean;
  formRequired: boolean;
  formikErrors?: FormikErrors<Address>;
  formikTouched?: FormikTouched<Address>;
  handleAddressUpdate: (address: Address) => void;
}

const AddressForm: FC<AddressFormPropType> = ({
  address1PlaceholderId,
  address2PlaceholderId,
  cityPlaceholderId,
  headerTitleId,
  statePlaceholderId,
  zipCodePlaceholderId,
  value,
  isClient,
  disableForm,
  formRequired,
  formikErrors,
  formikTouched,
  handleAddressUpdate,
}) => {
  const [zipCodes, setZipCodes] = useState<ZipCodeType[]>([]);
  const [zipCodeOptions, setZipCodeOptions] = useState<OptionType[]>([]);
  const [address1Focus, setAddress1Focus] = useState(false);
  const [address2Focus, setAddress2Focus] = useState(false);

  const changeTextDebouncer = useCallback(
    debounce((code: string) => fetchZipCodeDetail(code, isClient), 500),
    [],
  );

  const formatAddressLine = (text: string): string => {
    return text.replaceAll(/[^a-zA-Z0-9.,!#&*)] /g, '');
  };

  const fetchZipCodeDetail = (city: string, tbrApproved: boolean) => {
    getZipCodeDetails(city, tbrApproved)
      .then((response: ZipCodeResponse) => {
        setZipCodes(response.zipcodes);
        const codes: OptionType[] = [];
        response.zipcodes.forEach((code) => {
          codes.push({
            id: code.zipCode,
            label: `${code.city} - ${code.state}`,
          });
        });
        setZipCodeOptions(codes);
      })
      .catch(() => {
        const codes: OptionType[] = [];
        setZipCodeOptions(codes);
      });
  };
  return (
    <Grid container rowSpacing={'24px'} columnSpacing={'40px'}>
      {headerTitleId && (
        <Grid item xs={12}>
          <MediumTypography
            textColor="#2A4241"
            fontweight={600}
            labelid={headerTitleId}
            defaultlabel="Address"
          />
        </Grid>
      )}

      <Grid item xs={6}>
        <Textinput
          name="address1"
          labelid={address1PlaceholderId}
          defaultlabelid="Address Line 1"
          type="text"
          disabled={disableForm}
          Value={value.address1 ?? ''}
          inputProps={{
            maxLength: 75,
          }}
          onFocus={() => {
            setAddress1Focus(true);
          }}
          onblur={() => {
            setAddress1Focus(false);
          }}
          inputLabelProps={{
            shrink:
              (value.address1 !== null && value.address1.length > 0) ||
              address1Focus,
          }}
          handlechange={(text) => {
            handleAddressUpdate({
              ...value,
              address1: formatAddressLine(text),
            });
          }}
          Required={formRequired}
        />
        {formikErrors?.address1 && formikTouched?.address1 && formRequired && (
          <SmallTypography
            sxProps={{ color: 'red' }}
            labelId={formikErrors.address1}
            defaultLabelId="Address Line 1 is required"
          />
        )}
      </Grid>
      <Grid item xs={6}>
        <Textinput
          name="address2"
          Value={value.address2 ?? ''}
          type="text"
          disabled={disableForm}
          labelid={address2PlaceholderId}
          defaultlabelid="Address Line 2"
          inputProps={{
            maxLength: 75,
          }}
          onFocus={() => {
            setAddress2Focus(true);
          }}
          onblur={() => {
            setAddress2Focus(false);
          }}
          inputLabelProps={{
            shrink:
              (value.address2 !== null && value.address2.length > 0) ||
              address2Focus,
          }}
          handlechange={(text) => {
            handleAddressUpdate({
              ...value,
              address2: formatAddressLine(text),
            });
          }}
        />
      </Grid>
      <Grid item xs={4}>
        <AutocompleteSearch
          labelId={cityPlaceholderId}
          defaultId="City"
          selectedValue={typeof value.city === 'string' ? null : value.city}
          data={zipCodeOptions}
          freeSolo={true}
          type={'text'}
          disabled={disableForm}
          onSelectItem={(item: OptionType | null) => {
            if (item === null) {
              handleAddressUpdate({
                ...value,
                city: { id: '', label: '' },
                state: '',
              });
              setZipCodes([]);
              setZipCodeOptions([]);
            } else {
              const zipCode = zipCodes.find((zip) => zip.zipCode === item.id);
              if (zipCode) {
                handleAddressUpdate({
                  ...value,
                  city: { id: zipCode.zipCode, label: zipCode.city },
                  state: zipCode.state,
                });
                setZipCodes([]);
                setZipCodeOptions([]);
              }
            }
          }}
          onTextChange={(event: ChangeEvent<HTMLInputElement>) => {
            handleAddressUpdate({
              ...value,
              state: '',
            });
            changeTextDebouncer(event.target.value);
          }}
          renderOption={(props, option: OptionType) => {
            return (
              <li {...props}>
                <span>{option.label}</span>
              </li>
            );
          }}
          disableLabel={false}
          textFieldVariant="outlined"
          Required={formRequired}
        />
        {formikErrors?.city && formikTouched?.city && formRequired && (
          <SmallTypography
            sxProps={{ color: 'red' }}
            labelId={formikErrors.city}
            defaultLabelId="City is required"
          />
        )}
      </Grid>

      <Grid item xs={4}>
        <Textinput
          name="state"
          labelid={statePlaceholderId}
          defaultlabelid="State"
          Value={value.state ?? ''}
          disabled={disableForm}
          inputProps={{ readOnly: true }}
          Required={formRequired}
        />
        {formikErrors?.state && formikTouched?.state && formRequired && (
          <SmallTypography
            sxProps={{ color: 'red' }}
            labelId={formikErrors.state}
            defaultLabelId="State is required"
          />
        )}
      </Grid>

      <Grid item xs={4}>
        <Textinput
          name="zipCode"
          labelid={zipCodePlaceholderId}
          defaultlabelid="Zip Code"
          Value={value.zipCode ?? ''}
          disabled={disableForm}
          inputProps={{
            maxLength: 10,
          }}
          handlechange={(text) => {
            const digits = text.replaceAll(/\D/g, '');
            let formatted = digits;
            if (digits.length > 5) {
              formatted = digits.slice(0, 5) + '-' + digits.slice(5, 9);
            }
            handleAddressUpdate({
              ...value,
              zipCode: formatted,
            });
          }}
          Required={formRequired}
        />
        {formikErrors?.zipCode && formikTouched?.zipCode && formRequired && (
          <SmallTypography
            sxProps={{ color: 'red' }}
            labelId={formikErrors.zipCode}
            defaultLabelId="Zip code is required"
          />
        )}
      </Grid>
    </Grid>
  );
};

export default AddressForm;
